'use client';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import SeeMoreLink from '../../../app/(site)/quote/[ticker]/_components/SeeMoreLink';
import { seeMoreDestinations } from '../../../app/(site)/quote/[ticker]/_utils/seeMoreDestinations';
import type { ColumnDef } from '@benzinga/table';
import type { GovernmentTrade } from '@benzinga/gov-trades';
import { formatDate } from './formatters';
import QuoteListTable from './QuoteListTable';
import { TradeTypeBadge, govTransactionTypeTone } from './TradeTypeBadge';
import { formatTransaction } from '../GovTrades/ColumnsDef/formatTransaction';

const memberNameLinkClassName = 'font-semibold text-blue-600 no-underline hover:text-blue-800';

const memberProfileHref = (slug: string | undefined): string | null => {
  const trimmedSlug = slug?.trim();
  if (!trimmedSlug) {
    return null;
  }
  return `/government-trades/members/${encodeURIComponent(trimmedSlug)}`;
};

const CongressionalMemberName: React.FC<{ name: string; slug?: string }> = ({ name, slug }) => {
  const href = memberProfileHref(slug);
  if (!href) {
    return <>{name}</>;
  }

  return (
    <a
      className={memberNameLinkClassName}
      data-action="Quote Ownership Congressional Member Click"
      data-category="Quote Ownership"
      href={href}
    >
      {name}
    </a>
  );
};

export const CongressionalMemberCell: React.FC<{
  memberProfileSlugs: Record<string, string>;
  trade: GovernmentTrade;
}> = ({ memberProfileSlugs, trade }) => {
  const name = trade.filer_info?.display_name;
  if (!name) {
    return <>—</>;
  }

  return <CongressionalMemberName name={name} slug={memberProfileSlugs[name]} />;
};

const partyColor = (party: string | undefined) => {
  if (!party) return 'text-gray-500';
  const normalized = party.toLowerCase();
  if (normalized.includes('democrat') || normalized === 'd') return 'text-blue-600';
  if (normalized.includes('republican') || normalized === 'r') return 'text-red-600';
  return 'text-gray-500';
};

export const createGovTradesColumnsDef = (memberProfileSlugs: Record<string, string>): ColumnDef<GovernmentTrade>[] => [
  {
    cellClass: 'text-gray-600',
    field: 'transaction_date',
    headerName: 'Date',
    valueFormatter: ({ data }) => formatDate(data.transaction_date),
  },
  {
    cellClass: 'font-semibold text-gray-800',
    cellRenderer: ({ data }) => <CongressionalMemberCell memberProfileSlugs={memberProfileSlugs} trade={data} />,
    field: 'id',
    headerName: 'Member',
    valueFormatter: ({ data }) => data.filer_info?.display_name || '—',
  },
  {
    cellRenderer: ({ data }) => (
      <span className={`font-semibold ${partyColor(data.filer_info?.party)}`}>{data.filer_info?.party ?? '—'}</span>
    ),
    field: 'id',
    headerName: 'Party',
  },
  {
    cellClass: 'capitalize text-gray-600',
    field: 'chamber',
    headerName: 'Chamber',
    valueFormatter: ({ data }) => data.chamber ?? '—',
  },
  {
    cellRenderer: ({ data }) => (
      <TradeTypeBadge
        label={formatTransaction(data.transaction_type ?? '')}
        tone={govTransactionTypeTone(data.transaction_type)}
      />
    ),
    field: 'transaction_type',
    headerName: 'Action',
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'amount',
    headerClass: 'text-right',
    headerName: 'Amount',
    valueFormatter: ({ data }) => data.amount || '—',
  },
];

export const GovTradesTable: React.FC<{
  memberProfileSlugs: Record<string, string>;
  symbol: string;
  trades: GovernmentTrade[];
}> = ({ memberProfileSlugs, symbol, trades }) => {
  const { t } = useTranslation('quote');
  const columnsDef = useMemo(() => createGovTradesColumnsDef(memberProfileSlugs), [memberProfileSlugs]);
  const recentTrades = useMemo(
    () => [...trades].sort((a, b) => (b.transaction_date ?? '').localeCompare(a.transaction_date ?? '')).slice(0, 20),
    [trades],
  );

  if (!recentTrades.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <SectionTitle level={2} size="xl" uppercase={false}>
          Congressional Trading Activity
        </SectionTitle>
        <SeeMoreLink
          action="see_more_government_trades"
          href={seeMoreDestinations.governmentTrades(symbol)}
          label={t('Quote.SeeMore.government-trades')}
        />
      </div>
      <QuoteListTable columnsDef={columnsDef} gateType="gov-trades" rowData={recentTrades} />
    </div>
  );
};

export default GovTradesTable;
